import React, { useState } from "react";
import { Edit, ToggleLeft, ToggleRight, Plus, DollarSign, CreditCard, Wallet, Receipt, Smartphone, Landmark, Gift, Banknote, Trash2 } from "lucide-react";
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {SelectionBar, SettingsTable, StatusBadge, AddAction, ListLoader} from "./SettingsUI";
import Button from '../Common/Button';

const PaymentsList = ({
    showNotification,
    onEditPayment,
    onAddPayment,
    selectedItems,
    setSelectedItems,
    selectedAll,
    setSelectedAll,
    onDeleteSelected
}) => {
    const queryClient = useQueryClient();


    // Fetch payment methods
    const fetchPayments = async () => {
        const accountId = await secureStorage.getItem("selectedAccount");
        const response = await api.get("/payements", {
            headers: {
                "X-account-id": accountId.id,
                Accept: "application/json",
            },
        });
        return response.data;
    };

    const { data: payments, isLoading, error } = useQuery({
        queryKey: ["payments"],
        queryFn: fetchPayments,
    });

    // Toggle active status
    const togglePaymentStatus = useMutation({
        mutationFn: async (payment) => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const updatedPayment = { ...payment, active: !payment.active };
            await api.put(`/payements/${payment.id}`, updatedPayment, {
                headers: {
                    "X-account-id": accountId.id,
                    "Content-Type": "application/json",
                },
            });
            return updatedPayment;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["payments"]});
            showNotification("success", "Statut du mode de paiement mis à jour avec succès");
        },
        onError: () => {
            showNotification("error", "Erreur lors de la mise à jour du statut");
        },
    });

    // Delete payments mutation
    const deletePaymentsMutation = useMutation({
        mutationFn: async (paymentIds) => {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Delete payments one by one
            const results = await Promise.all(
                paymentIds.map(id =>
                    api.delete(`/payements/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        return { error: err, id };
                    })
                )
            );

            // Check for errors
            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                if (failures.length === paymentIds.length) {
                    throw new Error("Impossible de supprimer les modes de paiement sélectionnés");
                } else {
                    throw new Error(`${failures.length} sur ${paymentIds.length} modes de paiement n'ont pas pu être supprimés`);
                }
            }

            return paymentIds;
        },
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            // Reset selection and refetch data
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['payments']});
            showNotification('success', 'Modes de paiement supprimés avec succès');
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
        }
    });

    // Handle select all checkbox
    const handleSelectAll = () => {
        if (!payments || payments.length === 0) return;

        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? payments.map(item => item.id) : []);
    };

    // Handle individual item selection
    const toggleItem = (paymentId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(paymentId)
                ? prevSelected.filter((id) => id !== paymentId)
                : [...prevSelected, paymentId]
        );
    };

    // Get the appropriate icon component
    const getIconComponent = (iconName) => {
        switch (iconName) {
            case "creditcard": return <CreditCard className="w-5 h-5" />;
            case "cash": return <DollarSign className="w-5 h-5" />;
            case "wallet": return <Wallet className="w-5 h-5" />;
            case "receipt": return <Receipt className="w-5 h-5" />;
            case "mobile": return <Smartphone className="w-5 h-5" />;
            case "bank": return <Landmark className="w-5 h-5" />;
            case "gift": return <Gift className="w-5 h-5" />;
            case "check": return <Banknote className="w-5 h-5" />;
            default: return <Receipt className="w-5 h-5" />;
        }
    };

    // ids awaiting confirmation: the selection, or a single row from its menu
    const [pendingDelete, setPendingDelete] = useState(null);

    const confirmDelete = () => {
        deletePaymentsMutation.mutate(pendingDelete);
        setPendingDelete(null);
    };

    const rowActions = (payment) => [
        {label: 'Modifier', icon: Edit, onClick: () => onEditPayment(payment), tone: 'blue'},
        payment.active
            ? {label: 'Désactiver', icon: ToggleLeft, onClick: () => togglePaymentStatus.mutate(payment), tone: 'blue'}
            : {label: 'Activer', icon: ToggleRight, onClick: () => togglePaymentStatus.mutate(payment), tone: 'green'},
        {label: 'Supprimer', icon: Trash2, onClick: () => setPendingDelete([payment.id]), tone: 'red', danger: true},
    ];

    const columns = [
        {key: 'name', label: 'Nom', render: (payment) => (
            <span className="flex items-center gap-2 min-w-0">
                <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">{getIconComponent(payment.icon)}</span>
                <span className="truncate">{payment.name}</span>
            </span>
        )},
        {key: 'active', label: 'Statut', render: (payment) => <StatusBadge active={payment.active}/>},
    ];

    return (
        <div>
            <AddAction onClick={onAddPayment}/>


            <SelectionBar count={selectedItems.length} singular="mode de paiement sélectionné" plural="modes de paiement sélectionnés"
                          onDelete={() => setPendingDelete(selectedItems)} deleting={deletePaymentsMutation.isPending}/>

            {isLoading ? (
                <ListLoader label="Chargement des modes de paiement..."/>
            ) : error ? (
                <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={payments || []}
                    selection={{selected: selectedItems, onToggle: toggleItem, allSelected: selectedAll, onToggleAll: handleSelectAll}}
                    rowActions={rowActions}
                    onRowClick={onEditPayment}
                    empty={{
                        icon: CreditCard,
                        title: 'Aucun mode de paiement',
                        text: 'Ajoutez les moyens de paiement acceptés en caisse.',
                        action: (
                            <Button variant="primary" icon={Plus} className="mt-4" onClick={onAddPayment}>
                                Ajouter un mode de paiement
                            </Button>
                        ),
                    }}
                />
            )}

            {pendingDelete && (
                <ConfirmationDialog
                    title="Confirmation de suppression"
                    message={`Êtes-vous sûr de vouloir supprimer ${pendingDelete.length} mode${pendingDelete.length > 1 ? 's' : ''} de paiement ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDelete(null)}
                    danger={true}
                />
            )}
        </div>
    );
};

export default PaymentsList;
